<script setup>
import TenantAppointmentCell from '@frontJs/components/tables/cells/projectManagement/apikom/tenantReports/TenantAppointmentCell.vue';
import EyeIcon from '@iconify-vue/ph/eye';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ExportButton from '@js/components/buttons/ExportButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const appointmentFrequencyLabels = {
    monthly: 'Mensuel',
    bimonthly: 'Bimensuel',
    quarterly: 'Trimestriel',
    semiannual: 'Semestriel',
    yearly: 'Annuel',
};

const {
    group = undefined,
    type = undefined,
    ongoing,
    processed,
    callback = undefined,
} = defineProps({
    group: {
        required: false,
        type: String,
    },
    type: {
        required: false,
        type: String,
    },
    ongoing: {
        required: false,
        type: Boolean,
    },
    processed: {
        required: false,
        type: Boolean,
    },
    callback: {
        required: false,
        type: Function,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    selectionIds,
    exportData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'alerts.group',
            header: () => 'Groupe',
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.group.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'alerts.type',
            header: () => 'Type',
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.type.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'tenants.name',
            header: () => 'Client',
            cell: (props) =>
                props.row.original.tenant
                    ? h(TextCell, {
                          text: props.row.original.tenant.name,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'alerts.message',
            header: () => 'Message',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.message,
                    length: 3000,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'alerts.data->appointment_last_login_at',
            header: () => 'Dernière connexion',
            cell: (props) =>
                props.row.original.data.appointmentLastLoginAt
                    ? h(TextCell, {
                          text: props.row.original.data.appointmentLastLoginAt,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'alerts.data->tenant_appointment_frequency',
            header: () => 'Récurrence RDV',
            cell: (props) =>
                props.row.original.data.tenantAppointmentFrequency
                    ? h(PillCell, {
                          pillValue:
                              appointmentFrequencyLabels[
                                  props.row.original.data
                                      .tenantAppointmentFrequency
                              ],
                      })
                    : undefined,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'alerts.data->last_appointment',
            header: () => 'Dernier RDV',
            cell: (props) =>
                props.row.original.data.lastAppointment
                    ? h(TenantAppointmentCell, {
                          appointment: props.row.original.data.lastAppointment,
                      })
                    : undefined,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'alerts.data->appointment_mail_request_sent_at',
            header: () => 'Email',
            cell: (props) =>
                props.row.original.data.appointmentMailRequestSentAt
                    ? h(TextCell, {
                          text: props.row.original.data
                              .appointmentMailRequestSentAt,
                      })
                    : undefined,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'alerts.data->last_invoice_date',
            header: () => 'Date dernière facture',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.data.lastInvoiceDate,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'alerts.data->seo_backlink_platform_label',
            header: () => 'Plateforme',
            cell: (props) =>
                props.row.original.data &&
                props.row.original.data.seoBacklinkPlatformLabel
                    ? h(PillCell, {
                          pillValue:
                              props.row.original.data.seoBacklinkPlatformLabel,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'alerts.data->seo_backlink_date',
            header: () => 'Date',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.data.seoBacklinkDate,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'alerts.data->seo_backlink_source_url',
            header: () => 'URL source',
            cell: (props) =>
                h(LinkCell, {
                    text: new URL(props.row.original.data.seoBacklinkSourceUrl)
                        .hostname,
                    href: props.row.original.data.seoBacklinkSourceUrl,
                    maxWidth: 250,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'alerts.data->seo_backlink_checked_at',
            header: () => 'Vérifié le',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.data.seoBacklinkCheckedAt,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'alerts.createdAt',
            header: () => 'Créé le',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.createdAt,
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(
                                ActionButton,
                                {
                                    size: 'small',
                                    square: true,
                                    title: 'Voir',
                                    icon: EyeIcon,
                                    href: `/${props.row.original.tenantId}/login?token=${props.row.original.loginToken}&redirectLink=${window.btoa(props.row.original.referenceLink)}`,
                                    target: '_blank',
                                },
                                () => 'Voir',
                            ),
                    },
                ),
        }),
    ],
    url: route('front.postAlerts'),
    exportUrl: route('front.postExportAlerts'),
    data: {
        group,
        type,
        ongoing,
        processed,
    },
    columnVisibility: {
        'alerts.group': !group && !type,
        'alerts.type': !type,
        'alerts.data->appointment_last_login_at':
            type === 'appointment_to_make',
        'alerts.data->tenant_appointment_frequency':
            type === 'appointment_to_make',
        'alerts.data->last_appointment': type === 'appointment_to_make',
        'alerts.data->last_invoice_date': group === 'invoices',
        'alerts.data->appointment_mail_request_sent_at':
            type === 'appointment_to_make',
        'alerts.data->seo_backlink_date': type === 'dead_netlinking_link',
        'alerts.data->seo_backlink_source_url': type === 'dead_netlinking_link',
        'alerts.data->seo_backlink_checked_at': type === 'dead_netlinking_link',
        'alerts.data->seo_backlink_platform_label':
            type === 'dead_netlinking_link',
    },
    columnPinning: {
        right: ['actions'],
    },
    selectionIdKey: 'id',
    exportSelectionIdKey: 'alerts.id',
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ExportButton
                :csv-export-callback="() => exportData('csv')"
                :xlsx-export-callback="() => exportData('xlsx')"
                :disabled="selectionIds.length === 0"
            />
        </template>
    </DataTableLayout>
</template>
